import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Check, X, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppLayout from '@/components/layout/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [draftPlan, setDraftPlan] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchData = async () => {
    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userRes.data);

      const recRes = await axios.get(`${BACKEND_URL}/api/recommendations`, { withCredentials: true });
      if (recRes.data && recRes.data.length > 0) {
        setRecommendation(recRes.data[0]);
      }

      const chatRes = await axios.get(`${BACKEND_URL}/api/chat/history`, { withCredentials: true });
      setChatMessages(chatRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get phases from recommendation (support both old and new format)
  const phases = useMemo(() => {
    if (!recommendation) return [];
    
    // New format: phases array
    if (recommendation.phases && Array.isArray(recommendation.phases)) {
      return recommendation.phases;
    }
    
    // Old format: allocation_plan with phase strings
    if (recommendation.allocation_plan) {
      const phaseMap = {};
      recommendation.allocation_plan.forEach(item => {
        const phaseKey = item.phase || 'all';
        if (!phaseMap[phaseKey]) {
          phaseMap[phaseKey] = {
            phase_number: Object.keys(phaseMap).length + 1,
            name: phaseKey,
            allocations: []
          };
        }
        phaseMap[phaseKey].allocations.push(item);
      });
      return Object.values(phaseMap);
    }
    
    return [];
  }, [recommendation]);

  const monthlyBudget = recommendation?.monthly_budget || 
    phases[0]?.allocations?.reduce((sum, a) => sum + (a.monthly_amount || 0), 0) || 0;

  const handleLogout = async () => {
    await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    navigate('/');
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isSending) return;

    const userMsg = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsSending(true);

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/draft-recommendation`,
        { message: userMsg },
        { withCredentials: true }
      );
      
      if (res.data.draft_plan) {
        setDraftPlan(res.data.draft_plan);
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: res.data.response,
          hasDraft: true
        }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleApproveDraft = async () => {
    if (!draftPlan) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/chat/approve-draft`,
        { draft_plan: draftPlan },
        { withCredentials: true }
      );
      
      // Refresh recommendation
      const recRes = await axios.get(`${BACKEND_URL}/api/recommendations`, { withCredentials: true });
      if (recRes.data && recRes.data.length > 0) {
        setRecommendation(recRes.data[0]);
      }
      
      toast.success('Plan updated successfully!');
      setDraftPlan(null);
      
      setChatMessages(prev => prev.map((msg) => 
        msg.hasDraft ? { ...msg, hasDraft: false, content: msg.content + '\n\n✅ Changes approved and applied.' } : msg
      ));
    } catch (error) {
      console.error('Error approving draft:', error);
      toast.error('Failed to apply changes');
    }
  };

  const handleRejectDraft = () => {
    setDraftPlan(null);
    setChatMessages(prev => prev.map((msg) => 
      msg.hasDraft ? { ...msg, hasDraft: false, content: msg.content + '\n\n❌ Changes rejected.' } : msg
    ));
    toast.info('Draft rejected. Your plan remains unchanged.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AppLayout user={user} onLogout={handleLogout}>
      <div className="h-[calc(100vh-65px)] flex">
        {/* Chat Panel */}
        <div className="flex-1 flex flex-col">
          <div className="border-b px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/plan')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Chat with AI Advisor
                </h1>
                <p className="text-sm text-muted-foreground">Ask questions or request changes to your investment plan</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 px-6 py-4">
            <div className="max-w-3xl mx-auto space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-primary mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
                  <p className="text-muted-foreground mb-6">
                    Ask questions about your plan or request modifications
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {[
                      'Explain my current plan',
                      'Add Bitcoin to my portfolio',
                      'Why these allocations?',
                      'Change my debt payoff strategy'
                    ].map((suggestion) => (
                      <Button
                        key={suggestion}
                        variant="outline"
                        size="sm"
                        onClick={() => setChatInput(suggestion)}
                        className="text-xs"
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`p-4 rounded-lg max-w-[85%] ${
                    msg.role === 'user' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce"></div>
                      <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Draft Approval Banner */}
          {draftPlan && (
            <div className="border-t bg-yellow-50 dark:bg-yellow-900/20 px-6 py-4">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Draft Plan Ready</h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Review the proposed changes. Approve to update your plan or reject to keep current.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleRejectDraft}>
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button size="sm" onClick={handleApproveDraft} className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-1" />
                      Approve Changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t px-6 py-4">
            <div className="max-w-3xl mx-auto flex gap-3">
              <Input
                ref={inputRef}
                placeholder="Ask a question or request changes to your plan..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isSending || draftPlan !== null}
                className="flex-1"
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={isSending || !chatInput.trim() || draftPlan !== null}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Plan Summary Panel */}
        <div className="hidden lg:block w-96 border-l bg-muted/30 overflow-y-auto">
          <div className="p-6">
            <h2 className="text-lg font-bold mb-4">
              {draftPlan ? '📝 Draft Plan' : 'Current Plan'}
            </h2>
            
            {draftPlan ? (
              <DraftPlanView draftPlan={draftPlan} />
            ) : (
              <CurrentPlanView phases={phases} monthlyBudget={monthlyBudget} />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function CurrentPlanView({ phases, monthlyBudget }) {
  if (!phases || phases.length === 0) {
    return <p className="text-muted-foreground">No plan available</p>;
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">Monthly Budget</p>
        <p className="text-2xl font-bold font-mono">${monthlyBudget.toLocaleString()}</p>
      </Card>

      <div className="space-y-3">
        <h3 className="font-semibold text-sm">Your Plan Phases</h3>
        {phases.map((phase, idx) => {
          const monthRange = phase.month_end 
            ? `M${phase.month_start}-${phase.month_end}`
            : `M${phase.month_start}+`;
          
          return (
            <Card key={idx} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm">Phase {phase.phase_number}</p>
                  <p className="text-xs text-muted-foreground">{monthRange}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-primary mb-2">{phase.name}</p>
              <div className="space-y-1">
                {phase.allocations?.map((alloc, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {alloc.ticker && alloc.ticker !== 'N/A' ? alloc.ticker : alloc.product?.split(' ')[0]}
                    </span>
                    <span className="font-mono">{alloc.percentage}%</span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function DraftPlanView({ draftPlan }) {
  const phases = draftPlan.phases || [];
  const monthlyBudget = phases[0]?.allocations?.reduce((sum, a) => sum + (a.monthly_amount || 0), 0) || 0;
  
  return (
    <div className="space-y-4">
      <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
        <strong>⚠️ Draft Preview</strong>
        <p className="text-xs mt-1">This is a proposed change. Approve or reject below.</p>
      </div>

      <Card className="p-4 border-yellow-400">
        <p className="text-sm text-muted-foreground">Monthly Budget</p>
        <p className="text-2xl font-bold font-mono">${monthlyBudget.toLocaleString()}</p>
      </Card>

      <div className="space-y-3">
        <h3 className="font-semibold text-sm">Proposed Phases</h3>
        {phases.map((phase, idx) => {
          const monthRange = phase.month_end 
            ? `M${phase.month_start}-${phase.month_end}`
            : `M${phase.month_start}+`;
          
          return (
            <Card key={idx} className="p-4 border-yellow-400">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm">Phase {phase.phase_number}</p>
                  <p className="text-xs text-muted-foreground">{monthRange}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-primary mb-2">{phase.name}</p>
              <div className="space-y-1">
                {phase.allocations?.map((alloc, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {alloc.ticker && alloc.ticker !== 'N/A' ? alloc.ticker : alloc.product?.split(' ')[0]}
                    </span>
                    <span className="font-mono">{alloc.percentage}%</span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
